import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { StudentClass } from '../data/models/ot_nationals/otn_player';

// Combines clsx + tailwind-merge.
// Use for any component className to safely merge Tailwind utilities.
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

const StudentClassAbbrev: Record<StudentClass, string> = {
	[StudentClass.Freshman]: 'FR',
	[StudentClass.Sophomore]: 'SO',
	[StudentClass.Junior]: 'JR',
	[StudentClass.Senior]: 'SR',
	[StudentClass.Graduate]: 'GR',
};

export function getStudentClass(graduationYear?: number | null, options?: { abbreviated?: boolean }): string | null {
	if (!graduationYear) return null;

	const now = new Date();
	const year = now.getFullYear();
	const month = now.getMonth();

	// Treat school year as running from roughly Aug–May,
	// and "advancing" on June 1.
	const schoolYear = month >= 5 ? year : year - 1;

	const yearsUntilGrad = graduationYear - schoolYear;

	let classification: StudentClass | null;

	if (yearsUntilGrad <= 0) {
		classification = StudentClass.Graduate;
	} else if (yearsUntilGrad === 1) {
		classification = StudentClass.Senior;
	} else if (yearsUntilGrad === 2) {
		classification = StudentClass.Junior;
	} else if (yearsUntilGrad === 3) {
		classification = StudentClass.Sophomore;
	} else if (yearsUntilGrad === 4) {
		classification = StudentClass.Freshman;
	} else {
		classification = null;
	}

	if (!classification) return null;
	if (!options?.abbreviated) return classification;

	return StudentClassAbbrev[classification] ?? null;
}

export function formatHeight(inches?: number | null): string | null {
	if (inches == null || isNaN(inches)) return null;

	const feet = Math.floor(inches / 12);
	const remainingInches = inches % 12;

	return `${feet}’${remainingInches}”`;
}

export function getLocalDateParts(timestamp: string, options: Intl.DateTimeFormatOptions) {
	const date = new Date(timestamp);

	const parts = new Intl.DateTimeFormat('en-US', options).formatToParts(date);
	const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';

	return { parts, get };
}

export type DateFormatOptions = {
	weekday?: 'none' | 'short' | 'long';
	month?: 'numeric' | 'short' | 'long';
	day?: 'numeric' | 'ordinal';
	time?: boolean;
	timeZoneName?: boolean;
	prependAt?: boolean;
	/** IANA zone to format in; defaults to the runtime zone (the viewer's, in the browser). */
	timeZone?: string;
};

export function formatDate(timestamp: string, options: DateFormatOptions = {}): string {
	const {
		weekday = 'none',
		month = 'long',
		day = 'ordinal',
		time = false,
		timeZoneName = false,
		prependAt = false,
		timeZone,
	} = options;

	const intlOptions: Intl.DateTimeFormatOptions = { timeZone };

	if (weekday !== 'none') {
		intlOptions.weekday = weekday === 'short' ? 'short' : 'long';
	}

	intlOptions.month = month;
	intlOptions.day = 'numeric';

	if (time) {
		intlOptions.hour = 'numeric';
		intlOptions.minute = '2-digit';
		intlOptions.hour12 = true;
	}

	if (timeZoneName) {
		intlOptions.timeZoneName = 'short';
	}

	const { get } = getLocalDateParts(timestamp, intlOptions);

	const weekdayStr = weekday !== 'none' ? get('weekday') : '';
	const monthStr = get('month');
	const dayRaw = get('day');
	const dayNum = Number(dayRaw) || 0;

	let dayStr = dayRaw;
	if (day === 'ordinal' && dayNum) {
		const suffix =
			dayNum === 1 || dayNum === 21 || dayNum === 31
				? 'st'
				: dayNum === 2 || dayNum === 22
					? 'nd'
					: dayNum === 3 || dayNum === 23
						? 'rd'
						: 'th';
		dayStr = `${dayNum}${suffix}`;
	}

	let timeStr = '';
	if (time) {
		const hour = get('hour');
		const minute = get('minute');
		const dayPeriod = get('dayPeriod');
		const core = `${hour}:${minute} ${dayPeriod}`;
		timeStr = prependAt ? `@ ${core}` : core;
	}

	const zoneStr = timeZoneName ? get('timeZoneName') : '';

	return [weekdayStr, monthStr, dayStr, timeStr, zoneStr].filter(Boolean).join(' ').trim();
}

export function formatEventDateDisplay(timestamp: string): string {
	return formatDate(timestamp, {
		weekday: 'long',
		month: 'long',
		day: 'ordinal',
		time: false,
		timeZoneName: false,
	});
}

const stateAbbreviations: Record<string, string> = {
	alabama: 'AL',
	alaska: 'AK',
	arizona: 'AZ',
	arkansas: 'AR',
	california: 'CA',
	colorado: 'CO',
	connecticut: 'CT',
	delaware: 'DE',
	florida: 'FL',
	georgia: 'GA',
	hawaii: 'HI',
	idaho: 'ID',
	illinois: 'IL',
	indiana: 'IN',
	iowa: 'IA',
	kansas: 'KS',
	kentucky: 'KY',
	louisiana: 'LA',
	maine: 'ME',
	maryland: 'MD',
	massachusetts: 'MA',
	michigan: 'MI',
	minnesota: 'MN',
	mississippi: 'MS',
	missouri: 'MO',
	montana: 'MT',
	nebraska: 'NE',
	nevada: 'NV',
	'new hampshire': 'NH',
	'new jersey': 'NJ',
	'new mexico': 'NM',
	'new york': 'NY',
	'north carolina': 'NC',
	'north dakota': 'ND',
	ohio: 'OH',
	oklahoma: 'OK',
	oregon: 'OR',
	pennsylvania: 'PA',
	'rhode island': 'RI',
	'south carolina': 'SC',
	'south dakota': 'SD',
	tennessee: 'TN',
	texas: 'TX',
	utah: 'UT',
	vermont: 'VT',
	virginia: 'VA',
	washington: 'WA',
	'west virginia': 'WV',
	wisconsin: 'WI',
	wyoming: 'WY',
};

export function getStateAbbreviation(state?: string): string {
	if (!state) return '';
	const key = state.trim().toLowerCase();

	if (key.length === 2 && /^[a-z]{2}$/.test(key)) {
		return key.toUpperCase();
	}

	return stateAbbreviations[key] ?? state;
}

export const getClockFromMilliseconds = (milliseconds: number) => {
	if (milliseconds == null) {
		return '';
	}
	return `${Math.floor(milliseconds / 1000 / 60)}:${String(Math.floor((milliseconds / 1000) % 60)).padStart(2, '0')}`;
};

export const toNum = (value: unknown): number => {
	if (value === null || value === undefined || value === '') return 0;
	if (typeof value === 'number') return Number.isFinite(value) ? value : 0;
	if (typeof value === 'string') {
		const n = Number.parseFloat(value);
		return Number.isFinite(n) ? n : 0;
	}
	return 0;
};
